import { Fragment, ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Button from './Button';

export interface ConfirmDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
	title?: string;
	message: string;
	confirmLabel?: string;
	cancelLabel?: string;
	variant?: 'danger' | 'primary';
	isLoading?: boolean;
	showCancel?: boolean;
	icon?: ReactNode;
}

const ConfirmDialog = ({
	isOpen,
	onClose,
	onConfirm,
	title = 'Confirm',
	message,
	confirmLabel = 'Yes, Delete',
	cancelLabel = 'Cancel',
	variant = 'danger',
	isLoading = false,
	showCancel = true,
	icon,
}: ConfirmDialogProps) => {
	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-[60]" onClose={onClose}>
				{/* Backdrop */}
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-200"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-150"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
				</Transition.Child>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-200"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-150"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-2xl transition-all">
								<div className="flex items-start gap-4">
									<div
										className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
											variant === 'danger'
												? 'bg-red-100 dark:bg-red-900/30'
												: 'bg-indigo-100 dark:bg-indigo-900/30'
										}`}
									>
										{icon || (
											<ExclamationTriangleIcon
												className={`w-5 h-5 ${
													variant === 'danger'
														? 'text-red-600 dark:text-red-400'
														: 'text-indigo-600 dark:text-indigo-400'
												}`}
											/>
										)}
									</div>
									<div className="flex-1">
										<Dialog.Title
											as="h3"
											className="text-base font-semibold text-gray-900 dark:text-gray-100"
										>
											{title}
										</Dialog.Title>
										<p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
											{message}
										</p>
									</div>
								</div>

								<div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
									{showCancel && (
										<Button
											variant="secondary"
											onClick={onClose}
											disabled={isLoading}
										>
											{cancelLabel}
										</Button>
									)}
									<Button
										variant={variant === 'danger' ? 'danger' : 'primary'}
										onClick={onConfirm}
										isLoading={isLoading}
									>
										{confirmLabel}
									</Button>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
};

export default ConfirmDialog;
